import React, { useCallback, useState } from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import currency from "currency.js";
import {
  formatVariantOptionFactor,
  resolveVariantOptionForSize,
} from "../../common/variantSizeOverride";

/** Split size names on " • " / " · "; first part is the title, the rest join as one subtitle. */
function formatSizeColumnHeaderParts(name) {
  if (name == null || name === "") return [];
  return String(name)
    .split(/\s*[•·]\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function CellValue({ option, size }) {
  const { allowed, hasOverride, pricing } = resolveVariantOptionForSize(option, size.name);

  if (!allowed) {
    return <CancelIcon color="error" sx={{ fontSize: 20, flexShrink: 0 }} />;
  }

  const amount =
    pricing?.amount != null && String(pricing.amount).trim() !== "" && +pricing.amount !== 0
      ? pricing.amount
      : null;
  const factor =
    amount == null && pricing?.factor != null && +pricing.factor !== 0 ? pricing.factor : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
      }}
    >
      <CheckCircleIcon
        color={hasOverride ? "warning" : "success"}
        sx={{ fontSize: 20, flexShrink: 0 }}
      />
      {(amount != null || factor != null) && (
        <Typography component="span" variant="body2" sx={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
          {factor != null ? formatVariantOptionFactor(factor) : `+${currency(amount).format()}`}
        </Typography>
      )}
    </Box>
  );
}

export default function VariantOptionSizeOverrideGrid({
  sizeChart,
  options,
  onCellClick,
  onOptionEditDetails,
  onOptionEditPricing,
  onOptionDelete,
}) {
  const [optionMenuAnchor, setOptionMenuAnchor] = useState(null);
  const [optionMenuIndex, setOptionMenuIndex] = useState(null);

  const closeOptionMenu = useCallback(() => {
    setOptionMenuAnchor(null);
    setOptionMenuIndex(null);
  }, []);

  if (!sizeChart || sizeChart.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No size chart data available
      </Typography>
    );
  }

  return (
    <TableContainer
      sx={{
        width: "100%",
        mb: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "background.paper",
      }}
    >
      <Table stickyHeader sx={{ minWidth: 200 + sizeChart.length * 200 }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 4,
                width: 200,
                minWidth: 200,
                backgroundColor: "grey.100",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                Option
              </Typography>
            </TableCell>
            {sizeChart.map((size, sizeIndex) => {
              const headingParts = formatSizeColumnHeaderParts(size.name);
              const title = headingParts[0];
              const subtitle = headingParts.slice(1).join(" • ");
              return (
                <TableCell
                  key={`${size.name}-${sizeIndex}`}
                  sx={{
                    width: 200,
                    minWidth: 200,
                    verticalAlign: "top",
                    textAlign: "center",
                    backgroundColor: "grey.100",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 500,
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                    }}
                  >
                    {title}
                  </Typography>
                  {subtitle ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5, wordBreak: "break-word", lineHeight: 1.35 }}
                    >
                      {subtitle}
                    </Typography>
                  ) : null}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {(options || []).map((option, optionIndex) => (
            <TableRow key={option.id != null ? String(option.id) : `${option.name}-${optionIndex}`}>
              <TableCell
                component="th"
                scope="row"
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  width: 200,
                  minWidth: 200,
                  backgroundColor: "background.paper",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 600,
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                      fontSize: "0.95rem",
                    }}
                  >
                    {option.name}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={`Options for ${option.name || "option"}`}
                    aria-haspopup="true"
                    aria-expanded={
                      optionMenuIndex === optionIndex && Boolean(optionMenuAnchor)
                        ? "true"
                        : undefined
                    }
                    sx={{ color: "text.secondary", flexShrink: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOptionMenuAnchor(e.currentTarget);
                      setOptionMenuIndex(optionIndex);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
              {sizeChart.map((size, sizeIndex) => (
                <TableCell key={`${size.name}-${sizeIndex}`} sx={{ p: 0, textAlign: "center" }}>
                  <ButtonBase
                    aria-label={`Edit ${option.name || "option"} override for ${size.name}`}
                    onClick={() => onCellClick?.(optionIndex, size)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      minHeight: 64,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <CellValue option={option} size={size} />
                  </ButtonBase>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Menu
        anchorEl={optionMenuAnchor}
        open={Boolean(optionMenuAnchor) && optionMenuIndex !== null}
        onClose={closeOptionMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { py: 0.5, minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            const idx = optionMenuIndex;
            closeOptionMenu();
            onOptionEditDetails?.(idx);
          }}
          sx={{ px: 2, py: 1, minHeight: 44 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Edit Option Details"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const idx = optionMenuIndex;
            closeOptionMenu();
            onOptionEditPricing?.(idx);
          }}
          sx={{ px: 2, py: 1, minHeight: 44 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AttachMoneyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Pricing" primaryTypographyProps={{ variant: "body2" }} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const idx = optionMenuIndex;
            closeOptionMenu();
            onOptionDelete?.(idx);
          }}
          sx={{ px: 2, py: 1, minHeight: 44 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" primaryTypographyProps={{ variant: "body2" }} />
        </MenuItem>
      </Menu>
    </TableContainer>
  );
}
